import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import ActionBar from '../components/ActionBar';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import ReviewsSection from '../components/ReviewsSection';
import CustomCalendar from '../components/CustomCalendar';
import { CalendarDays, Users, Search, Wifi, Flame, Leaf, ArrowRight, MapPin, Shield, Phone } from 'lucide-react';
import { format } from 'date-fns';
import { tr, enUS } from 'date-fns/locale';
import { toast } from 'sonner';
import { saveSearch, loadSearch, toFutureDate } from '../lib/searchState';
import axios from 'axios';

const HERO_IMAGE = 'https://images.unsplash.com/photo-1476231682828-37e571bc172f?crop=entropy&cs=srgb&fm=jpg&q=90&w=1920';
const FIN_EVI_IMAGE = 'https://customer-assets.emergentagent.com/job_hotel-reserve-57/artifacts/w5gs9c18_cddb049dcc4b215d0b8c71090b9973448f4db731.jpeg';
const AERIAL_IMAGE = 'https://customer-assets.emergentagent.com/job_hotel-reserve-57/artifacts/4vrwgswc_iesdkwukaqi2q3ionehj.jpg';

export default function HomePage() {
  const { t, language, formatPrice, BACKEND_URL, currency, getImageUrl } = useApp();
  const navigate = useNavigate();
  const [featuredRooms, setFeaturedRooms] = useState([]);

  // Booking state (persisted across pages until user changes it)
  const saved = loadSearch();
  const [checkIn, setCheckIn] = useState(toFutureDate(saved?.checkIn));
  const [checkOut, setCheckOut] = useState(toFutureDate(saved?.checkOut));
  const [guests, setGuests] = useState(saved?.guests || 2);
  const [childAges, setChildAges] = useState(Array.isArray(saved?.children) ? saved.children : []);
  const [guestsOpen, setGuestsOpen] = useState(false);
  const [checkInOpen, setCheckInOpen] = useState(false);
  const [checkOutOpen, setCheckOutOpen] = useState(false);

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const locale = language === 'tr' ? tr : enUS;

  useEffect(() => {
    saveSearch({
      checkIn: checkIn ? format(checkIn, 'yyyy-MM-dd') : null,
      checkOut: checkOut ? format(checkOut, 'yyyy-MM-dd') : null,
      guests,
      children: childAges,
    });
  }, [checkIn, checkOut, guests, childAges]);

  const nights = checkIn && checkOut
    ? Math.round((checkOut - checkIn) / (1000 * 60 * 60 * 24)) : 0;

  // Set page title
  useEffect(() => {
    document.title = language === 'tr'
      ? 'Amorium Hotel Kastamonu | 4 Mevsim Doğa Tatili | Online Rezervasyon'
      : 'Amorium Hotel Kastamonu | 4 Seasons Nature Holiday | Online Booking';
  }, [language]);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/rooms`).then(res => {
      setFeaturedRooms(res.data.slice(0, 3));
    }).catch(() => {});
  }, [BACKEND_URL]);

  const handleSearch = () => {
    if (!checkIn || !checkOut) { toast.error(t('dates_required')); return; }
    const params = new URLSearchParams({
      check_in: format(checkIn, 'yyyy-MM-dd'),
      check_out: format(checkOut, 'yyyy-MM-dd'),
      guests: String(guests),
    });
    if (childAges.length > 0) params.set('children', childAges.join(','));
    navigate(`/rooms?${params.toString()}`);
  };

  const features = language === 'tr' ? [
    { icon: Leaf, title: 'Küre Dağları\'nda', desc: 'Kastamonu\'nun eşsiz doğasında, çam ormanları arasında' },
    { icon: Flame, title: 'Ahşap Fin Evleri', desc: 'Özel şömine, panoramik pencereler ve özel bahçe' },
    { icon: Wifi, title: 'Lüks Konfor', desc: 'Jakuzi, ücretsiz WiFi, zengin kahvaltı dahil' },
    { icon: Shield, title: 'Güvenli Rezervasyon', desc: '3D Secure güvenceli ödeme, ücretsiz iptal imkanı' },
  ] : [
    { icon: Leaf, title: 'Küre Mountains', desc: 'Unique nature of Kastamonu, among pine forests' },
    { icon: Flame, title: 'Finnish Log Houses', desc: 'Private fireplace, panoramic windows and private garden' },
    { icon: Wifi, title: 'Luxury Comfort', desc: 'Jacuzzi, free WiFi, rich breakfast included' },
    { icon: Shield, title: 'Secure Booking', desc: 'iyzico secure payment, free cancellation option' },
  ];

  return (
    <div style={{ minHeight: '100vh', background: '#F6F1E7', fontFamily: 'Manrope, sans-serif' }}>
      {/* ========== ACTION BAR (STICKY TOP) ========== */}
      <ActionBar />
      
      {/* ========== HERO SECTION ========== */}
      <section style={{ position: 'relative', height: 'min(85vh, 700px)', minHeight: '500px', overflow: 'hidden' }}>
        <Navbar transparent={true} />

        {/* Background image */}
        <img
          src={HERO_IMAGE}
          alt="Küre Dağları - Amorium Hotel"
          style={{
            position: 'absolute', inset: 0, width: '100%', height: '100%',
            objectFit: 'cover', objectPosition: 'center',
          }}
        />
        {/* Overlay */}
        <div style={{
          position: 'absolute', inset: 0,
          background: 'linear-gradient(135deg, rgba(31,58,46,0.75) 0%, rgba(0,0,0,0.45) 50%, rgba(31,58,46,0.65) 100%)',
        }} />

        {/* Hero Content */}
        <div style={{
          position: 'relative', zIndex: 2,
          display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center',
          height: '100%',
          textAlign: 'center',
          padding: '80px 1.5rem 60px',
        }}>
          {/* Badge */}
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: '6px',
            background: 'rgba(176, 141, 87, 0.25)',
            border: '1px solid rgba(176, 141, 87, 0.5)',
            borderRadius: '30px',
            padding: '6px 16px',
            marginBottom: '20px',
          }}>
            <MapPin size={13} style={{ color: '#B08D57' }} />
            <span style={{ fontSize: '12px', fontWeight: 600, color: 'rgba(255,255,255,0.9)', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
              {language === 'tr' ? 'Kastamonu · Amorium Hotel' : 'Kastamonu · Amorium Hotel'}
            </span>
          </div>

          <h1 style={{
            fontFamily: 'Cormorant Garamond, Georgia, serif',
            fontSize: 'clamp(36px, 6vw, 72px)',
            fontWeight: 600,
            color: '#FFFFFF',
            lineHeight: 1.1,
            marginBottom: '12px',
            textShadow: '0 2px 20px rgba(0,0,0,0.3)',
            maxWidth: '800px',
          }}>
            {language === 'tr' ? 'Doğanın Kalbinde' : 'In the Heart of Nature'}
          </h1>
          <h2 style={{
            fontFamily: 'Cormorant Garamond, Georgia, serif',
            fontSize: 'clamp(22px, 3.5vw, 40px)',
            fontWeight: 400,
            fontStyle: 'italic',
            color: '#B08D57',
            marginBottom: '20px',
            textShadow: '0 2px 10px rgba(0,0,0,0.4)',
          }}>
            {language === 'tr' ? 'Lüks Bir Kaçış' : 'A Luxurious Escape'}
          </h2>

          <p style={{
            fontSize: 'clamp(14px, 1.8vw, 16px)',
            color: 'rgba(255,255,255,0.85)',
            maxWidth: '560px',
            lineHeight: 1.7,
            marginBottom: '32px',
          }}>
            {language === 'tr'
              ? 'Ahşap Fin evleri ve tarihi taş binalarda, doğayla bütünleşen lüks bir konaklama deneyimi.'
              : 'A luxury accommodation experience integrated with nature, in Finnish wooden houses and historic stone buildings.'}
          </p>

          {/* CTA Buttons */}
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', justifyContent: 'center' }}>
            <button
              onClick={() => navigate('/rooms')}
              style={{
                background: '#B08D57', color: 'white',
                fontSize: '14px', fontWeight: 700,
                padding: '14px 32px', borderRadius: '12px',
                border: 'none', cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: '8px',
                transition: 'background 0.2s',
                fontFamily: 'Manrope, sans-serif',
              }}
            >
              {language === 'tr' ? 'Odaları Keşfet' : 'Explore Rooms'}
              <ArrowRight size={16} />
            </button>
            <button
              onClick={() => document.getElementById('booking-widget')?.scrollIntoView({ behavior: 'smooth' })}
              style={{
                background: 'rgba(255,255,255,0.15)',
                border: '1px solid rgba(255,255,255,0.4)',
                color: 'white', fontSize: '14px', fontWeight: 600,
                padding: '14px 32px', borderRadius: '12px',
                cursor: 'pointer',
                backdropFilter: 'blur(10px)',
                fontFamily: 'Manrope, sans-serif',
              }}
            >
              {language === 'tr' ? 'Rezervasyon Yap' : 'Book Now'}
            </button>
          </div>
        </div>
      </section>

      {/* ========== BOOKING WIDGET - INLINE CALENDAR (no floating popovers) ========== */}
      <div id="booking-widget" style={{ background: '#F6F1E7', padding: '0 1rem' }}>
        <div style={{ maxWidth: '1000px', margin: '0 auto', transform: 'translateY(-40px)' }}>
          <div style={{ background: '#FFFFFF', borderRadius: '20px', boxShadow: '0 20px 60px rgba(31,58,46,0.15)', border: '1px solid rgba(216,209,198,0.5)', overflow: 'hidden' }}>
            {/* Header */}
            <div style={{ background: '#1F3A2E', padding: '14px 24px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <CalendarDays size={16} style={{ color: '#B08D57' }} />
              <span style={{ fontSize: '13px', fontWeight: 700, color: 'white', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                {language === 'tr' ? 'Müsaitlik Kontrolü & Rezervasyon' : 'Check Availability & Book'}
              </span>
            </div>

            {/* Fields Row */}
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'stretch' }}>
              {/* Check-in */}
              <button
                data-testid="booking-bar-checkin-button"
                onClick={() => { setCheckInOpen(!checkInOpen); setCheckOutOpen(false); setGuestsOpen(false); }}
                style={{ flex: '1 1 180px', display: 'flex', alignItems: 'center', gap: '14px', padding: '20px 24px', textAlign: 'left', cursor: 'pointer', background: checkInOpen ? '#F0FAF5' : (checkIn ? '#F6FFF8' : 'transparent'), border: 'none', borderRight: '1px solid #EFE7DA', borderBottom: checkInOpen ? '2px solid #1F3A2E' : '2px solid transparent', transition: 'all 0.15s' }}
              >
                <div style={{ width: '40px', height: '40px', borderRadius: '12px', background: checkInOpen ? '#1F3A2E' : '#EFE7DA', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <CalendarDays size={18} style={{ color: checkInOpen ? '#B08D57' : '#B08D57' }} />
                </div>
                <div>
                  <p style={{ fontSize: '11px', fontWeight: 700, color: '#6E7F5B', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '4px' }}>{t('check_in')}</p>
                  <p style={{ fontSize: '15px', fontWeight: checkIn ? 600 : 400, color: checkIn ? '#1F3A2E' : '#B0A99A' }}>
                    {checkIn ? format(checkIn, 'dd MMMM yyyy', { locale }) : (language === 'tr' ? 'Tarih seçin' : 'Select date')}
                  </p>
                </div>
              </button>

              {/* Check-out */}
              <button
                data-testid="booking-bar-checkout-button"
                onClick={() => { setCheckOutOpen(!checkOutOpen); setCheckInOpen(false); setGuestsOpen(false); }}
                style={{ flex: '1 1 180px', display: 'flex', alignItems: 'center', gap: '14px', padding: '20px 24px', textAlign: 'left', cursor: 'pointer', background: checkOutOpen ? '#F0FAF5' : (checkOut ? '#F6FFF8' : 'transparent'), border: 'none', borderRight: '1px solid #EFE7DA', borderBottom: checkOutOpen ? '2px solid #1F3A2E' : '2px solid transparent', transition: 'all 0.15s' }}
              >
                <div style={{ width: '40px', height: '40px', borderRadius: '12px', background: checkOutOpen ? '#1F3A2E' : '#EFE7DA', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <CalendarDays size={18} style={{ color: '#B08D57' }} />
                </div>
                <div style={{ flex: 1 }}>
                  <p style={{ fontSize: '11px', fontWeight: 700, color: '#6E7F5B', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '4px' }}>{t('check_out')}</p>
                  <p style={{ fontSize: '15px', fontWeight: checkOut ? 600 : 400, color: checkOut ? '#1F3A2E' : '#B0A99A' }}>
                    {checkOut ? format(checkOut, 'dd MMMM yyyy', { locale }) : (language === 'tr' ? 'Tarih seçin' : 'Select date')}
                  </p>
                </div>
                {nights > 0 && <span style={{ background: '#1F3A2E', color: 'white', fontSize: '12px', fontWeight: 700, padding: '3px 10px', borderRadius: '20px', flexShrink: 0 }}>{nights}g</span>}
              </button>

              {/* Guests */}
              <button
                data-testid="booking-bar-guests-select"
                onClick={() => { setGuestsOpen(!guestsOpen); setCheckInOpen(false); setCheckOutOpen(false); }}
                style={{ width: '160px', flexShrink: 0, display: 'flex', alignItems: 'center', gap: '12px', padding: '20px 20px', textAlign: 'left', cursor: 'pointer', background: guestsOpen ? '#F0FAF5' : 'transparent', border: 'none', borderRight: '1px solid #EFE7DA', borderBottom: guestsOpen ? '2px solid #1F3A2E' : '2px solid transparent', transition: 'all 0.15s' }}
              >
                <div style={{ width: '40px', height: '40px', borderRadius: '12px', background: '#EFE7DA', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Users size={18} style={{ color: '#B08D57' }} />
                </div>
                <div>
                  <p style={{ fontSize: '11px', fontWeight: 700, color: '#6E7F5B', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '4px' }}>{t('guests')}</p>
                  <p style={{ fontSize: '15px', fontWeight: 600, color: '#1F3A2E' }}>
                    {guests} {language === 'tr' ? 'Yetişkin' : (guests > 1 ? 'Adults' : 'Adult')}{childAges.length > 0 ? `, ${childAges.length} ${language === 'tr' ? 'Çocuk' : (childAges.length > 1 ? 'Children' : 'Child')}` : ''}
                  </p>
                </div>
              </button>

              {/* Search */}
              <div style={{ display: 'flex', alignItems: 'center', padding: '12px 16px', flexShrink: 0 }}>
                <button
                  data-testid="booking-bar-search-button"
                  onClick={handleSearch}
                  style={{ background: '#1F3A2E', color: 'white', fontSize: '14px', fontWeight: 700, padding: '16px 28px', borderRadius: '14px', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', fontFamily: 'Manrope, sans-serif', boxShadow: '0 4px 16px rgba(31,58,46,0.3)', whiteSpace: 'nowrap' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#3E5A3F'}
                  onMouseLeave={e => e.currentTarget.style.background = '#1F3A2E'}
                >
                  <Search size={18} />
                  {language === 'tr' ? 'Müsaitliği Kontrol Et' : 'Check Availability'}
                </button>
              </div>
            </div>

            {/* INLINE CALENDAR - expands below fields, NO floating! */}
            {(checkInOpen || checkOutOpen) && (
              <div style={{ borderTop: '1px solid #EFE7DA', padding: '16px 24px', background: '#FAFAF8', display: 'flex', flexWrap: 'wrap', gap: '16px', alignItems: 'flex-start' }}>
                <div>
                  <p style={{ fontSize: '12px', fontWeight: 700, color: '#1F3A2E', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                    {checkInOpen ? (language === 'tr' ? '📅 Giriş Tarihi Seçin' : '📅 Select Check-in Date') : (language === 'tr' ? '📅 Çıkış Tarihi Seçin' : '📅 Select Check-out Date')}
                  </p>
                  <CustomCalendar
                    selected={checkInOpen ? checkIn : checkOut}
                    onSelect={d => {
                      if (checkInOpen) {
                        setCheckIn(d);
                        if (checkOut && d >= checkOut) setCheckOut(null);
                        setCheckInOpen(false);
                        setCheckOutOpen(true);
                      } else {
                        setCheckOut(d);
                        setCheckOutOpen(false);
                      }
                    }}
                    disabled={checkInOpen ? (d => { const t = new Date(); t.setHours(0,0,0,0); return d < t; }) : (d => d <= (checkIn || today))}
                    language={language}
                  />
                  <button onClick={() => { setCheckInOpen(false); setCheckOutOpen(false); }} style={{ display: 'block', marginTop: '8px', fontSize: '12px', color: '#6E7F5B', background: 'transparent', border: 'none', cursor: 'pointer', fontFamily: 'Manrope, sans-serif' }}>
                    ✕ {language === 'tr' ? 'Kapat' : 'Close'}
                  </button>
                </div>
              </div>
            )}

            {/* Guests dropdown - inline */}
            {guestsOpen && (
              <div style={{ borderTop: '1px solid #EFE7DA', padding: '16px 24px', background: '#FAFAF8' }} data-testid="guest-selector-inline">
                <p style={{ fontSize: '12px', fontWeight: 700, color: '#1F3A2E', marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  👥 {language === 'tr' ? 'Yetişkin Sayısı' : 'Number of Adults'}
                </p>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '14px' }}>
                  {[1,2,3,4,5,6].map(n => (
                    <button key={n} onClick={() => setGuests(n)}
                      data-testid={`home-adults-${n}`}
                      style={{ padding: '8px 18px', borderRadius: '20px', border: '1.5px solid', fontWeight: 600, fontSize: '14px', cursor: 'pointer', fontFamily: 'Manrope, sans-serif', background: guests === n ? '#1F3A2E' : 'white', color: guests === n ? 'white' : '#1F3A2E', borderColor: guests === n ? '#1F3A2E' : '#D8D1C6' }}>
                      {n} {language === 'tr' ? 'Yetişkin' : (n > 1 ? 'Adults' : 'Adult')}
                    </button>
                  ))}
                </div>
                <p style={{ fontSize: '12px', fontWeight: 700, color: '#1F3A2E', marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  🧒 {language === 'tr' ? 'Çocuk Sayısı (0-11 yaş)' : 'Number of Children (0-11)'}
                </p>
                <p style={{ fontSize: '11px', color: '#6E7F5B', marginTop: '-6px', marginBottom: '10px' }}>
                  {language === 'tr' ? '12 yaş ve üzeri yetişkin sayılır' : 'Age 12+ counts as adult'}
                </p>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: childAges.length > 0 ? '14px' : '0' }}>
                  {[0,1,2,3,4].map(n => (
                    <button key={n}
                      onClick={() => setChildAges(prev => n > prev.length ? [...prev, ...Array(n - prev.length).fill(5)] : prev.slice(0, n))}
                      data-testid={`home-children-${n}`}
                      style={{ padding: '8px 18px', borderRadius: '20px', border: '1.5px solid', fontWeight: 600, fontSize: '14px', cursor: 'pointer', fontFamily: 'Manrope, sans-serif', background: childAges.length === n ? '#B08D57' : 'white', color: childAges.length === n ? 'white' : '#1F3A2E', borderColor: childAges.length === n ? '#B08D57' : '#D8D1C6' }}>
                      {n === 0 ? (language === 'tr' ? 'Yok' : 'None') : `${n} ${language === 'tr' ? 'Çocuk' : (n > 1 ? 'Children' : 'Child')}`}
                    </button>
                  ))}
                </div>
                {childAges.length > 0 && (
                  <>
                    <p style={{ fontSize: '12px', fontWeight: 700, color: '#1F3A2E', marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                      {language === 'tr' ? 'Çocuk Yaşları' : 'Child Ages'}
                    </p>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '14px' }}>
                      {childAges.map((age, i) => (
                        <select key={i} value={age}
                          onChange={e => setChildAges(prev => prev.map((a, j) => j === i ? parseInt(e.target.value) : a))}
                          style={{ padding: '8px 10px', borderRadius: '10px', border: '1.5px solid #D8D1C6', fontSize: '14px', fontFamily: 'Manrope, sans-serif', background: 'white', cursor: 'pointer' }}
                          data-testid={`home-child-age-${i}`}>
                          {Array.from({ length: 12 }, (_, a) => (
                            <option key={a} value={a}>{`${i + 1}. ${language === 'tr' ? 'çocuk' : 'child'}: ${a} ${language === 'tr' ? 'yaş' : 'yrs'}`}</option>
                          ))}
                        </select>
                      ))}
                    </div>
                  </>
                )}
                <button onClick={() => setGuestsOpen(false)}
                  data-testid="home-guests-done"
                  style={{ background: '#1F3A2E', color: 'white', border: 'none', borderRadius: '10px', padding: '9px 24px', fontSize: '13px', fontWeight: 700, cursor: 'pointer', fontFamily: 'Manrope, sans-serif' }}>
                  {language === 'tr' ? 'Tamam' : 'Done'}
                </button>
              </div>
            )}

            {/* Footer */}
            <div style={{ background: '#F9F7F3', borderTop: '1px solid #EFE7DA', padding: '10px 24px', display: 'flex', flexWrap: 'wrap', gap: '16px', alignItems: 'center' }}>
              {[
                { icon: '✓', text: language === 'tr' ? 'Ücretsiz iptal' : 'Free cancellation', color: '#166534' },
                { icon: '🔒', text: language === 'tr' ? 'Güvenli ödeme' : 'Secure payment', color: '#1e40af' },
                { icon: '🌟', text: language === 'tr' ? 'En iyi fiyat garantisi' : 'Best price guarantee', color: '#B08D57' },
                { icon: '📞', text: language === 'tr' ? '7/24 destek' : '24/7 support', color: '#6E7F5B' },
              ].map((item, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span style={{ fontSize: '12px' }}>{item.icon}</span>
                  <span style={{ fontSize: '12px', color: item.color, fontWeight: 600 }}>{item.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ========== FEATURES SECTION ========== */}
      <section style={{ background: '#F6F1E7', padding: '1rem 1.5rem 5rem' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem' }}>
            {features.map((f, i) => (
              <div key={i} style={{
                background: 'white', borderRadius: '16px', padding: '24px 20px',
                border: '1px solid #EFE7DA',
                display: 'flex', alignItems: 'flex-start', gap: '14px',
              }}>
                <div style={{
                  width: '44px', height: '44px', borderRadius: '12px',
                  background: '#1F3A2E', flexShrink: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <f.icon size={20} style={{ color: '#B08D57' }} />
                </div>
                <div>
                  <p style={{ fontSize: '14px', fontWeight: 700, color: '#1F3A2E', marginBottom: '4px' }}>{f.title}</p>
                  <p style={{ fontSize: '12px', color: '#6E7F5B', lineHeight: 1.5 }}>{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== ROOMS SECTION ========== */}
      <section style={{ background: '#EFE7DA', padding: '5rem 1.5rem' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
            <div style={{ display: 'inline-block', height: '2px', width: '40px', background: '#B08D57', marginBottom: '16px' }} />
            <h2 style={{
              fontFamily: 'Cormorant Garamond, Georgia, serif',
              fontSize: 'clamp(28px, 4vw, 42px)',
              color: '#1F3A2E', marginBottom: '8px', fontWeight: 600,
            }}>
              {language === 'tr' ? 'Konaklama Seçeneklerimiz' : 'Our Accommodation Options'}
            </h2>
            <p style={{ fontSize: '14px', color: '#6E7F5B', maxWidth: '480px', margin: '0 auto' }}>
              {language === 'tr'
                ? 'Doğayla bütünleşen 5 farklı oda tipimizden size en uygun olanı seçin'
                : 'Choose from 5 room types integrated with nature'}
            </p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1.5rem' }}>
            {featuredRooms.map(room => (
              <div
                key={room._id}
                style={{
                  background: 'white', borderRadius: '20px', overflow: 'hidden',
                  border: '1px solid rgba(216,209,198,0.5)',
                  boxShadow: '0 4px 20px rgba(31,58,46,0.06)',
                  cursor: 'pointer', transition: 'transform 0.2s, box-shadow 0.2s',
                }}
                onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = '0 12px 40px rgba(31,58,46,0.12)'; }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 20px rgba(31,58,46,0.06)'; }}
                onClick={() => navigate(`/rooms/${room._id}`)}
              >
                <div style={{ position: 'relative', height: '220px', overflow: 'hidden' }}>
                  <img
                    src={getImageUrl(room.images?.[0])}
                    alt={room.name}
                    style={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 0.4s' }}
                  />
                  {/* Price badge */}
                  <div style={{
                    position: 'absolute', bottom: '12px', right: '12px',
                    background: 'rgba(255,255,255,0.97)',
                    borderRadius: '10px', padding: '6px 14px',
                    boxShadow: '0 2px 12px rgba(0,0,0,0.15)',
                  }}>
                    <span style={{ fontSize: '15px', fontWeight: 800, color: '#1F3A2E' }}>{formatPrice(room.base_price)}</span>
                    <span style={{ fontSize: '11px', color: '#6E7F5B', marginLeft: '3px' }}>{t('per_night')}</span>
                  </div>
                  {/* Room type badge */}
                  <div style={{
                    position: 'absolute', top: '12px', left: '12px',
                    background: 'rgba(31,58,46,0.88)',
                    borderRadius: '20px', padding: '4px 12px',
                  }}>
                    <span style={{ fontSize: '11px', fontWeight: 700, color: 'white' }}>{room.name}</span>
                  </div>
                </div>
                <div style={{ padding: '20px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <h3 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '20px', fontWeight: 600, color: '#1F3A2E' }}>
                      {room.name}
                    </h3>
                    <span style={{ fontSize: '12px', color: '#6E7F5B' }}>👥 {room.capacity} · 📐 {room.size_m2}m²</span>
                  </div>
                  <p style={{ fontSize: '13px', color: '#5A5450', lineHeight: 1.6, marginBottom: '14px',
                    display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                    {language === 'tr' ? room.description_tr : room.description_en}
                  </p>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '16px' }}>
                    {(language === 'tr' ? room.amenities_tr : room.amenities_en)?.slice(0, 3).map((a, i) => (
                      <span key={i} style={{
                        fontSize: '11px', background: '#EFE7DA', color: '#3E5A3F',
                        padding: '3px 10px', borderRadius: '20px', fontWeight: 500,
                      }}>{a}</span>
                    ))}
                  </div>
                  <button
                    style={{
                      width: '100%', background: '#1F3A2E', color: 'white',
                      padding: '12px', borderRadius: '12px', border: 'none',
                      fontSize: '13px', fontWeight: 700, cursor: 'pointer',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                      transition: 'background 0.2s',
                      fontFamily: 'Manrope, sans-serif',
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = '#3E5A3F'}
                    onMouseLeave={e => e.currentTarget.style.background = '#1F3A2E'}
                    onClick={e => { e.stopPropagation(); navigate(`/rooms/${room._id}`); }}
                  >
                    {t('view_details')} <ArrowRight size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div style={{ textAlign: 'center', marginTop: '2.5rem' }}>
            <button
              onClick={() => navigate('/rooms')}
              style={{
                background: 'transparent', color: '#1F3A2E',
                fontSize: '14px', fontWeight: 700,
                padding: '14px 36px', borderRadius: '12px',
                border: '2px solid #1F3A2E', cursor: 'pointer',
                display: 'inline-flex', alignItems: 'center', gap: '8px',
                fontFamily: 'Manrope, sans-serif',
                transition: 'background 0.2s, color 0.2s',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = '#1F3A2E'; e.currentTarget.style.color = 'white'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#1F3A2E'; }}
            >
              {language === 'tr' ? 'Tüm 5 Odayı Gör' : 'View All 5 Rooms'}
              <ArrowRight size={16} />
            </button>
          </div>
        </div>
      </section>

      {/* ========== EXPERIENCE SECTION ========== */}
      <section style={{ background: '#F6F1E7', padding: '5rem 1.5rem' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '2rem',
            alignItems: 'center',
          }}>
            <div>
              <div style={{ display: 'inline-block', height: '2px', width: '40px', background: '#B08D57', marginBottom: '16px' }} />
              <h2 style={{
                fontFamily: 'Cormorant Garamond, Georgia, serif',
                fontSize: 'clamp(28px, 4vw, 42px)',
                color: '#1F3A2E', marginBottom: '16px', fontWeight: 600,
              }}>
                {language === 'tr' ? 'Amorium Deneyimi' : 'The Amorium Experience'}
              </h2>
              <p style={{ fontSize: '15px', color: '#5A5450', lineHeight: 1.8, marginBottom: '24px' }}>
                {language === 'tr'
                  ? 'Kastamonu\'nun Küre Dağları\'nda, çam ormanlarıyla çevrili eşsiz konumumuzda doğa ile lüksü bir araya getiriyoruz. Ahşap Fin evlerimiz ve tarihi taş binalarımızda kalıcı anılar yaratmanız için her şeyi düşündük.'
                  : 'In the Küre Mountains of Kastamonu, surrounded by pine forests, we bring together nature and luxury. We have thought of everything for you to create lasting memories in our Finnish wooden houses and historic stone buildings.'}
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                {[
                  { num: '5', label: language === 'tr' ? 'Oda Tipi' : 'Room Types' },
                  { num: '4.9★', label: language === 'tr' ? 'Google Puanı' : 'Google Rating' },
                  { num: '100%', label: language === 'tr' ? 'Memnuniyet' : 'Satisfaction' },
                  { num: '24/7', label: language === 'tr' ? 'Hizmet' : 'Service' },
                ].map((s, i) => (
                  <div key={i} style={{
                    background: 'white', borderRadius: '12px', padding: '16px',
                    border: '1px solid #EFE7DA', textAlign: 'center',
                  }}>
                    <p style={{ fontSize: '22px', fontWeight: 800, color: '#1F3A2E', fontFamily: 'Cormorant Garamond, serif' }}>{s.num}</p>
                    <p style={{ fontSize: '12px', color: '#6E7F5B' }}>{s.label}</p>
                  </div>
                ))}
              </div>
            </div>
            <div style={{ position: 'relative' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <img src={FIN_EVI_IMAGE} alt="Fin Evi" style={{ width: '100%', height: '220px', objectFit: 'cover', borderRadius: '16px' }} />
                <img src={AERIAL_IMAGE} alt="Amorium Hotel" style={{ width: '100%', height: '220px', objectFit: 'cover', borderRadius: '16px', marginTop: '32px' }} />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ========== REVIEWS SECTION ========== */}
      <ReviewsSection />

      {/* ========== CTA SECTION ========== */}
      <section style={{
        background: 'linear-gradient(135deg, #1F3A2E 0%, #3E5A3F 100%)',
        padding: '5rem 1.5rem',
        textAlign: 'center',
      }}>
        <div style={{ maxWidth: '600px', margin: '0 auto' }}>
          <h2 style={{
            fontFamily: 'Cormorant Garamond, Georgia, serif',
            fontSize: 'clamp(28px, 4vw, 42px)',
            color: 'white', marginBottom: '12px', fontWeight: 600,
          }}>
            {language === 'tr' ? 'Unutulmaz Bir Kaçış Sizi Bekliyor' : 'An Unforgettable Escape Awaits'}
          </h2>
          <p style={{ fontSize: '15px', color: 'rgba(255,255,255,0.75)', lineHeight: 1.7, marginBottom: '32px' }}>
            {language === 'tr'
              ? 'Hemen rezervasyon yapın, doğanın kalbinde lüks bir deneyim için Amorium Hotel\'i keşfedin.'
              : 'Book now and discover Amorium Hotel for a luxurious experience in the heart of nature.'}
          </p>
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button
              onClick={() => navigate('/rooms')}
              style={{
                background: '#B08D57', color: 'white',
                fontSize: '14px', fontWeight: 700,
                padding: '16px 40px', borderRadius: '12px',
                border: 'none', cursor: 'pointer',
                fontFamily: 'Manrope, sans-serif',
                transition: 'background 0.2s',
              }}
              onMouseEnter={e => e.currentTarget.style.background = '#9A7A4A'}
              onMouseLeave={e => e.currentTarget.style.background = '#B08D57'}
            >
              {language === 'tr' ? 'Rezervasyon Yap' : 'Book Now'} →
            </button>
            <a
              href="tel:+90000000000"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '8px',
                background: 'transparent',
                border: '1px solid rgba(255,255,255,0.4)',
                color: 'white', fontSize: '14px', fontWeight: 600,
                padding: '16px 32px', borderRadius: '12px',
                textDecoration: 'none', fontFamily: 'Manrope, sans-serif',
              }}
            >
              <Phone size={16} />
              {language === 'tr' ? 'Bizi Arayın' : 'Call Us'}
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
